import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Heart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatCAD, resolveImg } from "@/lib/api";
import { Stars } from "@/components/Stars";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const saved = has(product.id);
  const out = (product.stock ?? 0) <= 0;
  const low = !out && (product.stock ?? 0) <= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.07 }}
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col rounded-[1.75rem] border border-border/70 bg-white p-2.5 shadow-[0_1px_2px_rgba(15,74,35,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-leaf/40 hover:shadow-[0_24px_48px_-20px_rgba(15,74,35,0.28)]"
    >
      <Link to={`/product/${product.id}`} className="relative block overflow-hidden rounded-[1.35rem] bg-[#F6F1E6]">
        <img
          src={resolveImg(product.image_url) || undefined}
          alt={product.name}
          loading="lazy"
          className={`aspect-square w-full object-cover mix-blend-multiply transition-transform duration-700 ease-out group-hover:scale-[1.06] ${out ? "opacity-55 grayscale-[35%]" : ""}`}
        />
        {out && (
          <span className="absolute left-3 top-3 rounded-full bg-ink px-3 py-1 text-[11px] font-bold text-white">
            Sold out
          </span>
        )}
        {low && (
          <span className="absolute left-3 top-3 rounded-full bg-ochre px-3 py-1 text-[11px] font-bold text-ink">
            Only {product.stock} left
          </span>
        )}
        <button
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={(e) => { e.preventDefault(); toggle(product); }}
          aria-label={saved ? `Remove ${product.name} from favourites` : `Save ${product.name} to favourites`}
          aria-pressed={saved}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur transition-all duration-200 hover:scale-110 hover:bg-white active:scale-90"
        >
          <Heart className={`h-4 w-4 transition-colors ${saved ? "fill-clay text-clay" : "text-forest"}`} />
        </button>
      </Link>

      <div className="flex flex-1 flex-col px-2.5 pb-2 pt-4">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-leaf">{product.category}</span>
        <Link to={`/product/${product.id}`}>
          <h3 className="mt-1 line-clamp-2 min-h-[2.6em] font-sans text-[15px] font-semibold leading-snug tracking-normal text-ink transition-colors group-hover:text-forest">
            {product.name}
          </h3>
        </Link>
        {product.unit && <p className="mt-0.5 truncate text-xs text-muted-foreground">{product.unit}</p>}
        {product.rating_count > 0 && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <Stars value={product.rating_avg} size="h-3.5 w-3.5" />
            <span className="text-xs text-muted-foreground">({product.rating_count})</span>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="font-display text-xl font-semibold text-forest" data-testid={`price-${product.id}`}>
            {formatCAD(product.price_cents)}
          </span>
          {out ? (
            <button
              data-testid={`add-to-cart-${product.id}`}
              disabled
              className="inline-flex h-10 items-center rounded-full bg-muted px-4 text-xs font-semibold text-muted-foreground"
            >
              Sold out
            </button>
          ) : (
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={() => addItem(product, 1)}
              className="inline-flex h-10 items-center gap-1 rounded-full bg-forest pl-3 pr-4 text-xs font-bold text-white transition-all duration-200 hover:bg-clay active:scale-95"
              aria-label={`Add ${product.name} to cart`}
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
